import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, fmtDateTime } from "@/components/common";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { People, OPS } from "@/components/FbFormDialog";
import { assignable } from "@/lib/assignees";
import { Plus, Trash2, Globe, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";

const LABELS = { form: "Website form", course: "Course", city: "City", state: "State", college: "College", exam: "Entrance exam",
  category: "Category", country: "Country (study abroad)", email: "Email", web_source: "Application source" };
const FORMS = "Website Enquiry, Course Enquiry, Website Application, Study Abroad Enquiry";

function RuleCard({ r, i, n, users, setRule, move, remove }) {
  return (
    <div className="border border-slate-200 rounded-xl p-3 space-y-2 bg-white" data-testid={`web-rule-${i}`}>
      <div className="grid sm:grid-cols-[auto_1fr_160px_1fr_auto] gap-2 items-center">
        <span className="text-xs font-bold text-[#4F46E5] w-6">#{i + 1}</span>
        <Select value={r.field} onValueChange={(v) => setRule(i, { field: v })}><SelectTrigger data-testid={`web-rule-field-${i}`}><SelectValue placeholder="Field" /></SelectTrigger>
          <SelectContent className="bg-white">{Object.entries(LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
        <Select value={r.op || "equals"} onValueChange={(v) => setRule(i, { op: v })}><SelectTrigger data-testid={`web-rule-op-${i}`}><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">{OPS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
        <Input value={r.value || ""} onChange={(e) => setRule(i, { value: e.target.value })} placeholder={r.field === "form" ? "e.g. Course Enquiry" : "e.g. MBA"} data-testid={`web-rule-value-${i}`} />
        <div className="flex">
          <Button size="icon" variant="ghost" disabled={i === 0} onClick={() => move(i, -1)} data-testid={`web-rule-up-${i}`}><ArrowUp className="w-4 h-4" /></Button>
          <Button size="icon" variant="ghost" disabled={i === n - 1} onClick={() => move(i, 1)} data-testid={`web-rule-down-${i}`}><ArrowDown className="w-4 h-4" /></Button>
          <Button size="icon" variant="ghost" className="text-rose-600" onClick={() => remove(i)} data-testid={`web-rule-del-${i}`}><Trash2 className="w-4 h-4" /></Button>
        </div>
      </div>
      <People users={users} value={r.assignees || []} onChange={(v) => setRule(i, { assignees: v })} testid={`web-rule-people-${i}`} />
    </div>
  );
}

export default function WebsiteLeads() {
  const [cfg, setCfg] = useState(null);
  const [users, setUsers] = useState([]);
  useEffect(() => {
    api.get("/website-leads/rules").then(({ data }) => setCfg(data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    api.get("/users").then(({ data }) => setUsers(assignable(data))).catch(() => {});
  }, []);
  if (!cfg) return null;
  const setRule = (i, p) => setCfg({ ...cfg, rules: cfg.rules.map((r, j) => (j === i ? { ...r, ...p } : r)) });
  const move = (i, d) => { const rs = [...cfg.rules]; [rs[i], rs[i + d]] = [rs[i + d], rs[i]]; setCfg({ ...cfg, rules: rs }); };
  const remove = (i) => setCfg({ ...cfg, rules: cfg.rules.filter((_, j) => j !== i) });
  const save = async () => {
    try { await api.put("/website-leads/rules", { rules: cfg.rules, fallback: cfg.fallback }); toast.success("Website lead rules saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="max-w-4xl">
      <PageHeader title="Website Leads" subtitle="Enquiries from ohcampus.com arrive within 30 seconds and are assigned by these rules" testid="website-leads-header">
        <Button onClick={save} className="bg-[#4F46E5] hover:bg-[#4338CA]" data-testid="web-rules-save">Save rules</Button>
      </PageHeader>
      <div className="flex items-center gap-3 bg-sky-50 border border-sky-200 rounded-xl px-4 py-3 mb-5 text-sm text-sky-900" data-testid="web-sync-status">
        <Globe className="w-5 h-5 text-sky-600" />
        <div className="flex-1">{cfg.connected ? <>Connected to the website database · last check {cfg.synced_at ? fmtDateTime(cfg.synced_at) : "—"} · {cfg.created} leads imported</> : "Website database is connected only on the live CRM (crm.ohcampus.com)."}</div>
      </div>
      <h3 className="font-semibold text-slate-800 mb-1">Assignment rules</h3>
      <p className="text-xs text-slate-500 mb-3">Checked top to bottom — the first match wins and assigns round-robin among its counselors (counselors on leave are skipped). Website form values: {FORMS}.</p>
      <div className="space-y-2 mb-3">{cfg.rules.map((r, i) => <RuleCard key={i} r={r} i={i} n={cfg.rules.length} users={users} setRule={setRule} move={move} remove={remove} />)}</div>
      <Button size="sm" variant="outline" onClick={() => setCfg({ ...cfg, rules: [...cfg.rules, { field: "", op: "equals", value: "", assignees: [] }] })} data-testid="web-add-rule"><Plus className="w-4 h-4 mr-1" />Add rule</Button>
      <div className="mt-6"><Label>If no rule matches, round-robin to</Label>
        <People users={users} value={cfg.fallback || []} onChange={(v) => setCfg({ ...cfg, fallback: v })} testid="web-fallback" />
        <p className="text-[11px] text-slate-400 mt-1">Leave empty to use the CRM's normal least-busy auto-assignment.</p></div>
    </div>
  );
}
